/**
 * The commission ledger — every payment a referred customer made and what it
 * earned. This is the page a partner opens to check a number they disagree with,
 * so it shows the gross charge next to the cut, not just the cut.
 */
import { classNames } from '~/utils/classNames';
import {
  Cell,
  CellGrid,
  COMMISSION_LABEL,
  COMMISSION_TONE,
  EmptyState,
  KIND_LABEL,
  Panel,
  PageHead,
  fmtDate,
  money,
  usePortal,
} from './shared';

/**
 * Commissions record what was earned; withdrawals are tracked separately on the
 * Payouts page, so a commission never becomes "paid" — it just counts, or it
 * doesn't.
 */
const STATUS_HELP: Record<string, string> = {
  earned: 'Counted towards your balance.',
  void: 'Reversed — the underlying payment was refunded.',
};

export default function Earnings() {
  const { data } = usePortal();
  const { recentCommissions, balance, affiliate } = data;
  const commissions = recentCommissions ?? [];

  return (
    <div className="flex flex-col gap-7">
      <PageHead
        title="Earnings"
        subtitle={`Every eligible payment earns you ${affiliate!.commissionRateBps / 100}%. Subscriptions and credit purchases count; domain purchases don’t, since they’re resold near cost.`}
      />

      <CellGrid columns={3}>
        <Cell label="Available" value={money(balance?.availableCents ?? 0)} accent note="Ready to withdraw" />
        <Cell label="Paid out" value={money(balance?.paidOutCents ?? 0)} />
        <Cell label="Lifetime" value={money(balance?.lifetimeCents ?? 0)} />
      </CellGrid>

      <Panel title="Commission ledger" aside={<span className="ui-caption text-faint">Last {commissions.length}</span>}>
        {commissions.length > 0 ? (
          <div className="divide-y divide-hairline">
            {/* Header row — this is a ledger, so the columns get labels. */}
            <div className="hidden grid-cols-[1fr_auto_auto_auto] gap-4 px-5 py-2.5 sm:grid">
              <span className="ui-caption text-faint">Payment</span>
              <span className="ui-caption w-24 text-right text-faint">Charged</span>
              <span className="ui-caption w-24 text-right text-faint">You earned</span>
              <span className="ui-caption w-20 text-right text-faint">Status</span>
            </div>

            {commissions.map((c) => (
              <div
                key={c.id}
                className="grid grid-cols-1 gap-1 px-4 py-3.5 sm:grid-cols-[1fr_auto_auto_auto] sm:gap-4 sm:px-5"
              >
                <div className="min-w-0">
                  <div className="truncate text-[13px] text-primary">{KIND_LABEL[c.paymentKind] ?? c.paymentKind}</div>
                  <div className="mt-0.5 text-[11.5px] text-muted">{fmtDate(c.accruedAt)}</div>
                </div>
                {/* The header row is hidden on phones, so values carry their own labels there. */}
                <div className="text-[13px] tabular-nums text-secondary sm:w-24 sm:text-right">
                  <span className="text-muted sm:hidden">Charged </span>
                  {money(c.amountCents, c.currency)}
                </div>
                <div className="text-[13px] font-medium tabular-nums text-primary sm:w-24 sm:text-right">
                  <span className="font-normal text-muted sm:hidden">You earned </span>
                  {money(c.commissionCents, c.currency)}
                </div>
                <div className="sm:w-20 sm:text-right">
                  <span
                    className={classNames('ui-pill', COMMISSION_TONE[c.status] ?? 'text-muted border-subtle')}
                    title={STATUS_HELP[c.status]}
                  >
                    {COMMISSION_LABEL[c.status] ?? c.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            title="Nothing earned yet"
            body="Every payment from a customer you referred lands here, including their renewals."
          />
        )}
      </Panel>

      <div className="grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-hairline bg-hairline sm:grid-cols-2">
        {Object.entries(STATUS_HELP).map(([status, help]) => (
          <div key={status} className="flex items-baseline gap-2.5 bg-surface px-5 py-3">
            <span className={classNames('ui-pill shrink-0', COMMISSION_TONE[status])}>
              {COMMISSION_LABEL[status] ?? status}
            </span>
            <span className="text-[12px] leading-relaxed text-muted">{help}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
